import React from 'react';

import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';

import { useBaseWidget } from 'hooks/useBaseWidget';

import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import Slider, { marks } from 'components/common/Slider/Slider';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import CriticalAnalysisMilestone from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/CriticalAnalysisMilestone/criticalAnalysisMilestone';
import SliderWrapper from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramProgressOverview/SliderWrapper';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { LoaderContainer } from 'components/common';
import { useMilestonesDistributionLogic } from 'components/widgets/MilestonesDistribution/MilestonesDistribution.logic';
import { useMilestonesDistributionProvider } from 'components/widgets/MilestonesDistribution/MilestonesDistribution.provider';

const id = 'CriticalAnalysisMilestone';
const title = 'Milestones Distribution (Latest)';
const componentKey: WidgetsMapComponentKeys = 'milestonesWeightedSCurve';

export const MilestonesDistribution = ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IComponentProps) => {
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { ui, api } = useMilestonesDistributionProvider({
        widgetId,
        globalFilterData,
        localFilters,
        contractId,
        latestVersionId,
        projectId,
    });
    const { logic } = useMilestonesDistributionLogic({ ui, api });

    return (
        <ErrorBoundary>
            <WidgetWithTitle
                id={id}
                title={<WidgetTitle title={title} />}
                titleComponents={[
                    !widgetId && (
                        <SliderWrapper key={'SliderWrapper'}>
                            <Slider
                                key={'Slider'}
                                defaultValue={ui.state.wbsRangeFilter}
                                step={1}
                                min={1}
                                max={logic.versionMaxLevel}
                                marks={marks(1, logic.versionMaxLevel)}
                                onChangeCommitted={(e, value) => ui.methods.setWbsRangeFilter(value)}
                            />
                        </SliderWrapper>
                    ),
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                    />,
                    <ExportDropdown title={title} key={'export'} subTitle={subTitle} />,
                    <TooltipIcon key={'info'} tooltip={api.getMilestones.data?.info} />,
                    <ThreeDotsMenu
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={title}
                        shareLink={route}
                    />,
                ]}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && ui.pills.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} filters={ui.pills} />
                    ) : null,
                ]}
            >
                {api.getMilestones.data && <CriticalAnalysisMilestone milestones={api.getMilestones.data} />}
                {api.getMilestones.isFetching && !api.getMilestones.data && (
                    <LoaderContainer>
                        <OverlayWithSpinner />
                    </LoaderContainer>
                )}
                {!api.getMilestones.isFetching && !api.getMilestones.data && <NoData />}
            </WidgetWithTitle>
        </ErrorBoundary>
    );
};
